import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { invalidateSurfaceCaches } from "@/react/features/promoteSurface/api/promoteSurface.api";
import {
  activityParamsForRange,
  coverageKeys,
  hubsParamsForFilter,
} from "./coverage.query-keys";
import {
  fetchCoverageActivity,
  fetchCoverageHubAssets,
  fetchCoverageHubDetail,
  fetchCoverageHubs,
  fetchCoverageHubWorkFiles,
  fetchCoverageSummary,
  generateCoverageWorkFile,
  removeCoverageContentRole,
} from "./coverage.service";
import { rejectSurfaceAsset } from "@/react/features/promoteSurface/api/promoteSurface.service";
import { invalidateMarketingAssets } from "@/react/components/entityViews/marketing/api/marketingAssets.cache";
import type {
  CoverageActivityRange,
  CoverageFilter,
  FetchCoverageAssetsParams,
  FetchCoverageWorkFilesParams,
} from "./types";

export function useCoverageSummaryQuery(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: coverageKeys.summary(slug ?? ""),
    queryFn: () => fetchCoverageSummary(slug!),
    enabled: !!slug && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCoverageHubsQuery(
  slug: string | undefined,
  filter: CoverageFilter,
  search: string,
  enabled = true
) {
  const params = hubsParamsForFilter(filter, search);
  return useQuery({
    queryKey: coverageKeys.hubs(slug ?? "", params),
    queryFn: () => fetchCoverageHubs(slug!, params),
    enabled: !!slug && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCoverageActivityQuery(
  slug: string | undefined,
  range: CoverageActivityRange,
  enabled = true
) {
  const params = activityParamsForRange(range);
  return useQuery({
    queryKey: coverageKeys.activity(slug ?? "", params),
    queryFn: () => fetchCoverageActivity(slug!, params),
    enabled: !!slug && enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCoverageHubDetailQuery(
  slug: string | undefined,
  hubId: number | null,
  enabled = true
) {
  return useQuery({
    queryKey: coverageKeys.hub(slug ?? "", hubId ?? 0),
    queryFn: () => fetchCoverageHubDetail(slug!, hubId!),
    enabled: !!slug && hubId != null && enabled,
    // Do not keep prior hub detail — avoids flashing the wrong hub while switching.
  });
}

export function useCoverageHubAssetsQuery(
  slug: string | undefined,
  hubId: number | null,
  params: FetchCoverageAssetsParams = {},
  enabled = true
) {
  const queryParams = { limit: 100, offset: 0, ...params };
  return useQuery({
    queryKey: coverageKeys.assets(slug ?? "", hubId ?? 0, queryParams),
    queryFn: () => fetchCoverageHubAssets(slug!, hubId!, queryParams),
    enabled: !!slug && hubId != null && enabled,
  });
}

export function useCoverageHubWorkFilesQuery(
  slug: string | undefined,
  hubId: number | null,
  params: FetchCoverageWorkFilesParams = {},
  enabled = true
) {
  const queryParams = { limit: 50, offset: 0, ...params };
  return useQuery({
    queryKey: coverageKeys.workFiles(slug ?? "", hubId ?? 0, queryParams),
    queryFn: () => fetchCoverageHubWorkFiles(slug!, hubId!, queryParams),
    enabled: !!slug && hubId != null && enabled,
  });
}

async function invalidateCoverageBoard(slug: string) {
  await queryClient.invalidateQueries({ queryKey: coverageKeys.board(slug) });
}

async function invalidateAfterRoleChange(slug: string, hubId: number | null) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coverageKeys.summary(slug) }),
    queryClient.invalidateQueries({
      queryKey: [...coverageKeys.board(slug), "hubs"],
    }),
    queryClient.invalidateQueries({
      queryKey: [...coverageKeys.board(slug), "activity"],
    }),
    hubId != null
      ? queryClient.invalidateQueries({ queryKey: coverageKeys.hub(slug, hubId) })
      : Promise.resolve(),
    hubId != null
      ? queryClient.invalidateQueries({
          queryKey: [...coverageKeys.board(slug), "hub", hubId, "assets"],
        })
      : Promise.resolve(),
  ]);
}

async function invalidateAfterWorkFile(slug: string, hubId: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coverageKeys.hub(slug, hubId) }),
    queryClient.invalidateQueries({
      queryKey: [...coverageKeys.board(slug), "hubs"],
    }),
    queryClient.invalidateQueries({
      queryKey: [...coverageKeys.board(slug), "hub", hubId, "work-files"],
    }),
  ]);
}

export function useCoverageMutations(
  slug: string | undefined,
  selectedHubId: number | null,
  boardId?: number | null,
) {
  const removeContentRole = useMutation({
    mutationFn: async (input: { assetId: number; tagId: number }) => {
      if (!slug) throw new Error("Missing board slug");
      return removeCoverageContentRole(slug, input.assetId, input.tagId);
    },
    onSuccess: async () => {
      if (slug) await invalidateAfterRoleChange(slug, selectedHubId);
    },
  });

  const demoteAsset = useMutation({
    mutationFn: async (assetId: number) => {
      if (!slug) throw new Error("Missing board slug");
      return rejectSurfaceAsset(slug, assetId);
    },
    onSuccess: async () => {
      if (!slug) return;
      await Promise.all([
        boardId != null ? invalidateMarketingAssets(boardId) : Promise.resolve(),
        invalidateCoverageBoard(slug),
        invalidateSurfaceCaches(slug),
      ]);
    },
  });

  const generateWorkFile = useMutation({
    mutationFn: async (input: { problemHubId: number; name: string }) => {
      if (!slug) throw new Error("Missing board slug");
      return generateCoverageWorkFile(slug, {
        problem_hub_id: input.problemHubId,
        name: input.name,
      });
    },
  });

  return {
    removeContentRole,
    demoteAsset,
    generateWorkFile,
    invalidateCoverageBoard: () => slug && invalidateCoverageBoard(slug),
    invalidateAfterWorkFile: (hubId: number) =>
      slug && invalidateAfterWorkFile(slug, hubId),
  };
}

export { coverageKeys, invalidateCoverageBoard };
